import { useNavigation } from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { SvgXml } from 'react-native-svg';
import { SCANS_LOGO_RATIO, SCANS_LOGO_SVG } from '~/assets/scansLogo';
import { AccountPill } from '~/components/AccountPill';
import { JourneyCard } from '~/components/JourneyCard';
import { Screen } from '~/components/Screen';
import { Notice, PLUS, PrimaryButton } from '~/components/ui';
import { useAuth } from '~/hooks/useAuth';
import { api, type JobListItem } from '~/lib/api';
import { isRunning } from '~/lib/jobStatus';
import { logger } from '~/lib/logger';
import { useTheme } from '~/theme/ThemeProvider';

const LIMIT = 5;

/** Same cadence as useJob: the contract says poll no faster than every 5 s. */
const POLL_MS = 5000;

/**
 * Landing screen: the last five journeys, and the way into a new one.
 *
 * Replaces the old neutral home. It is the answer to "I closed the app while it was
 * thinking" — a running job stays visible here, so a submitted inspection is never lost
 * just because the operator navigated away. That matters because a journey costs real
 * money and takes real time; there is no cheap way to check on one otherwise.
 *
 * Five is the API's own list limit, not an arbitrary cut.
 */
export function RecentJourneysScreen() {
  const { c, t, space } = useTheme();
  const nav = useNavigation<{ navigate: (s: string) => void }>();
  const auth = useAuth();
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const user = auth.status === 'authenticated' ? auth.user : null;
  const who = user?.name?.trim() || user?.upn || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listJobs(LIMIT);
      setJobs(r.jobs);
      setError(null);
    } catch (e) {
      logger.error('[RecentJourneys] load failed', e);
      setError(e instanceof Error ? e.message : 'Could not load recent journeys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
    "Updates on its own" is a promise the screen has to keep.

    It polls only while something is actually running, and stops the moment nothing is —
    five terminal cards do not need re-fetching every five seconds, and a phone at a
    vehicle is on someone's data. `jobs` is the dependency, so finishing the last running
    job tears the interval down on the very next render.
  */
  const anyRunning = jobs.some((j) => isRunning(j.status));
  useEffect(() => {
    if (!anyRunning) return;
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [anyRunning, load]);

  // `isRunning` is typed on JobStatus and shared with the jobs list, so the two screens
  // cannot drift and a new status cannot slip through as "still analysing".

  return (
    <Screen>
      <View style={{ paddingHorizontal: space.md, paddingTop: space.sm }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: space.md,
            height: 56,
          }}
        >
          <SvgXml xml={SCANS_LOGO_SVG} width={94} height={94 / SCANS_LOGO_RATIO} />
          <AccountPill who={who} onPress={() => void auth.signOut()} />
        </View>
        <Text style={[t.h1, { color: c.text }]}>Recent journeys</Text>
        <Text style={[t.body, { color: c.dim, lineHeight: 24 }]}>
          Your last {LIMIT} · updates on its own
        </Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: space.md, gap: space.sm }}>
        {error ? (
          <Notice testID="journeys-error" tone="stateFailed" title="Could not load your journeys.">
            {error}
          </Notice>
        ) : null}
        {!loading && jobs.length === 0 && !error ? (
          <Text testID="journeys-empty" style={[t.body, { color: c.dim }]}>
            No journeys yet. The first one you submit will appear here.
          </Text>
        ) : null}
        {jobs.map((j) => (
          <JourneyCard
            key={j.job_id}
            job={j}
            testID={`journey-${j.job_id}`}
            onPress={() => nav.navigate('Jobs')}
          />
        ))}
      </ScrollView>

      <View style={{ paddingHorizontal: space.md, paddingBottom: space.md, gap: space.sm }}>
        <PrimaryButton
          testID="new-inspection"
          label="New inspection"
          glyph={PLUS}
          onPress={() => nav.navigate('Inspect')}
        />
      </View>
    </Screen>
  );
}
